import React, { ComponentPropsWithRef } from "react"
import { Col, Container, Hidden, Row, ScreenClassRender, Visible } from "react-grid-system"
import styled from "styled-components"
import H2 from "../../atoms/typography/H2"
import { MotionValue, motion, useScroll, useSpring, useTransform } from "framer-motion"
import { HorizontalSpacer } from "../../atoms/spacers"

import { MediaQuery } from "../../../utils/design-helpers"
import H1 from "../../atoms/typography/H1"
import { theme } from "../../theme"
import Body from "../../atoms/typography/Body"
import H0 from "../../atoms/typography/H0"

const ScrollHeight = styled.div`
  background-color: ${({ theme }) => theme.colors.greyLighter};
  position: relative;
  min-height: 200vh;
  /* padding-inline: ${({ theme }) => theme.spacings.huge}; */

  /* ${MediaQuery.XS} {
    padding-inline: ${({ theme }) => theme.spacings.large};
  } */
`

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StepContent = styled.div`
  width: 100%;
  height: 100%;
  padding-inline: ${({ theme }) => theme.spacings.large};
  position: relative;
  z-index: 1;
`

const EllipseWrapper = styled.div`
  position: absolute;
  left: 25%;
  top: 50%;

  ${MediaQuery.LG} {
    left: 50%;
  }
`

type StepProps = {
  step: number
  numSteps: number
  progress: MotionValue<number>
  title: string
  bullets: string[]
  gradient: string
}

const Step = ({ progress, step, numSteps, title, bullets, gradient }: StepProps) => {
  const Ellipse = () => [<Ellipse1 />, <Ellipse2 />, <Ellipse3 />, <Ellipse4 />][step]

  const steppedProgress = useTransform(progress, [0, 1], [0, numSteps])
  const discreteProgress = useTransform(steppedProgress, (value) => Math.round(value))

  const steps = [...Array(numSteps)].map((_, i) => i)
  const thresholds = steps.map((i) => (i === step ? 1 : 0))
  const active = useSpring(useTransform(discreteProgress, steps, thresholds), {
    bounce: 0,
  })
  const diff = useTransform(
    useTransform(steppedProgress, (value) => Math.abs(step - value)),
    [0, numSteps],
    [1, 0],
  )

  const opacity = active
  const color = useTransform(diff, [1, 0], ["#252525", "#d3d3d33b"])
  const scale = useTransform(active, [1, 0], [1, 0.75])
  const transform = useTransform(active, [1, 0], ["translateX(-1px)", "translateX(0px)"])

  return (
    <Col xl={12 / numSteps}>
      <Row align="stretch" justify="center">
        <StepContent>
          <Row>
            <Col xs="content" xxl="content">
              <motion.div style={{ scale }}>
                <ScreenClassRender
                  render={(screen) => (
                    <Row justify={["xxl"].includes(screen) ? "end" : "start"}>
                      <H1>
                        <motion.span style={{ opacity }}>{step + 1}</motion.span>
                      </H1>
                    </Row>
                  )}
                />
              </motion.div>
            </Col>
            <Col xs={0.5} />
            <Col>
              <H2>
                <motion.span style={{ color }}>{title}</motion.span>
              </H2>
              <HorizontalSpacer spacing={1} />
              <motion.div style={{ opacity, transform }}>
                {bullets.map((description) => (
                  <div key={description}>
                    <Body>
                      <motion.span style={{ color }}>{description}</motion.span>
                    </Body>
                  </div>
                ))}
              </motion.div>
            </Col>
          </Row>
        </StepContent>
        <motion.div style={{ opacity }}>
          <ScreenClassRender
            render={(screen) => (
              <EllipseWrapper
                style={{
                  transform: `translate(-50%, -50%) scale(${["xs", "sm"].includes(screen) ? "0.5" : "1"})`,
                }}
              >
                <Ellipse />
              </EllipseWrapper>
            )}
          />
        </motion.div>
      </Row>
    </Col>
  )
}

const NewHowItWorksBanner: React.FC = () => {
  const target = React.useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    offset: ["center end", "end end"],
    target,
  })

  const steps: Pick<React.ComponentProps<typeof Step>, "bullets" | "gradient" | "title">[] = [
    {
      bullets: ["Choose a terminal", "Select a data package", "Pick accessories if needed"],
      gradient: "linear-gradient(97.08deg, #81A6C8 7.1%, #97D1D9 84.66%)",
      title: "Select your products",
    },
    {
      bullets: [
        "Purchase your equipment directly, apply for leasing or rental",
        "Create an account to manage your terminal and data plan",
      ],
      gradient: "linear-gradient(68deg, #80B67D 0%, #AED37C 100%)",
      title: "Select payment method ",
    },
    {
      bullets: [
        "You will receive your terminal approximately within 30 days from order confirmation",
        "Activate your terminal with your account",
      ],
      gradient: "linear-gradient(122deg, #C67C66 0%, #EAAD81 100%)",
      title: "Activate your terminal",
    },
    {
      bullets: ["Connect to satellite broadband in under a minute"],
      gradient: "linear-gradient(68deg, #EABB90 0%, #F3EB73 100%)",
      title: "Connect to the world!",
    },
  ]

  const intersperseSpacers = (items: JSX.Element[]) =>
    items
      .flatMap((item, i) => [
        <>
          <Visible xs sm>
            <HorizontalSpacer key={i} spacing={3} style={{ width: "100%" }} />
          </Visible>
          <Visible md>
            <HorizontalSpacer key={i} spacing={5} style={{ width: "100%" }} />
          </Visible>
        </>,
        item,
      ])
      .slice(1)

  return (
    <Container>
      <ScrollHeight ref={target}>
        <Sticky>
          <Hidden xl>
            <HorizontalSpacer spacing={2} />
          </Hidden>
          <H1 bold={true}>How to get connected:</H1>
          <HorizontalSpacer spacing={8} />
          <Container fluid>
            <Row align="stretch" justify="between">
              {intersperseSpacers(
                steps.map((step, i) => (
                  <Step {...step} step={i} key={step.title} numSteps={steps.length} progress={scrollYProgress} />
                )),
              )}
            </Row>
          </Container>
        </Sticky>
      </ScrollHeight>
    </Container>
  )
}

export default NewHowItWorksBanner

const Ellipse1 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="599" height="667" fill="none" viewBox="0 0 599 667">
    <g filter="url(#filter0_f_1105_783)">
      <ellipse
        cx="299.318"
        cy="333.024"
        fill="url(#paint0_linear_1105_783)"
        rx="180.5"
        ry="248"
        transform="rotate(30 299.318 333.024)"
      ></ellipse>
    </g>
    <defs>
      <filter
        id="filter0_f_1105_783"
        width="599.07"
        height="666.047"
        x="-0.217"
        y="0"
        colorInterpolationFilters="sRGB"
        filterUnits="userSpaceOnUse"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
        <feGaussianBlur result="effect1_foregroundBlur_1105_783" stdDeviation="50"></feGaussianBlur>
      </filter>
      <linearGradient
        id="paint0_linear_1105_783"
        x1="118.818"
        x2="481.157"
        y1="445.752"
        y2="428.533"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EABB90"></stop>
        <stop offset="1" stopColor="#F3EB73"></stop>
      </linearGradient>
    </defs>
  </svg>
)

const Ellipse2 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="765" height="643" fill="none" viewBox="0 0 765 643">
    <g filter="url(#filter0_f_1105_932)">
      <ellipse
        cx="382.55"
        cy="321.893"
        fill="url(#paint0_linear_1105_932)"
        rx="238.054"
        ry="162.25"
        transform="rotate(18.007 382.55 321.893)"
      ></ellipse>
    </g>
    <defs>
      <filter
        id="filter0_f_1105_932"
        width="763.863"
        height="641.984"
        x="0.617"
        y="0.898"
        colorInterpolationFilters="sRGB"
        filterUnits="userSpaceOnUse"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
        <feGaussianBlur result="effect1_foregroundBlur_1105_932" stdDeviation="75"></feGaussianBlur>
      </filter>
      <linearGradient
        id="paint0_linear_1105_932"
        x1="171.762"
        x2="573.538"
        y1="218.643"
        y2="291.846"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#81A6C8"></stop>
        <stop offset="1" stopColor="#97D1D9"></stop>
      </linearGradient>
    </defs>
  </svg>
)

const Ellipse3 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="677" height="577" fill="none" viewBox="0 0 677 577">
    <g filter="url(#filter0_f_1105_1010)">
      <ellipse
        cx="338.725"
        cy="288.418"
        fill="url(#paint0_linear_1105_1010)"
        rx="163.908"
        ry="221.404"
        transform="rotate(105 338.725 288.418)"
      ></ellipse>
    </g>
    <defs>
      <filter
        id="filter0_f_1105_1010"
        width="676.137"
        height="576.836"
        x="0.656"
        y="0"
        colorInterpolationFilters="sRGB"
        filterUnits="userSpaceOnUse"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
        <feGaussianBlur result="effect1_foregroundBlur_1105_1010" stdDeviation="60"></feGaussianBlur>
      </filter>
      <linearGradient
        id="paint0_linear_1105_1010"
        x1="187.814"
        x2="492.518"
        y1="132.429"
        y2="153.364"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#C67C66"></stop>
        <stop offset="1" stopColor="#EAAD81"></stop>
      </linearGradient>
    </defs>
  </svg>
)

const Ellipse4 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="679" height="548" fill="none" viewBox="0 0 679 548">
    <g filter="url(#filter0_f_1105_1028)">
      <ellipse
        cx="339.5"
        cy="274"
        fill="url(#paint0_linear_1105_1028)"
        rx="209.5"
        ry="144"
        transform="rotate(-180 339.5 274)"
      ></ellipse>
    </g>
    <defs>
      <filter
        id="filter0_f_1105_1028"
        width="679"
        height="548"
        x="0"
        y="0"
        colorInterpolationFilters="sRGB"
        filterUnits="userSpaceOnUse"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
        <feGaussianBlur result="effect1_foregroundBlur_1105_1028" stdDeviation="65"></feGaussianBlur>
      </filter>
      <linearGradient
        id="paint0_linear_1105_1028"
        x1="130"
        x2="547.735"
        y1="339.455"
        y2="299.773"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#80B67D"></stop>
        <stop offset="1" stopColor="#AED37C"></stop>
      </linearGradient>
    </defs>
  </svg>
)
